/**
 * 端末の右パネル。基本情報 + USB モード(admin が変える。null = 設定の既定に従う)+ 直近スキャン 5 件 + 直近 USB 20 件
 * + アップロード観測の状態(ブラウザごとの遮断・DoH)と直近の検知 20 件。
 * operator は「スキャンを依頼」をここからも出せる(端末を選び直さなくてよい)。
 * admin は「端末の鍵をリセット」を出せる(エージェントを入れ直すと鍵が失われ、以後 401 になるため)。
 */
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Popconfirm, Select, Space, message } from "antd";
import { useState } from "react";
import { Link } from "react-router-dom";

import type { DlpUsbMode } from "../../lib/api";
import { useApi } from "../../lib/useApi";
import { KeyValueList, PanelSection, SidePanel, TimeAgo } from "../../ui";
import {
  browserPolicyTag,
  bytesText,
  employeeCell,
  scanStatusTag,
  uploadAttributionTag,
  uploadKindTag,
  uploadStateTag,
  usbKindTag,
  usbModeTag,
} from "./bits";
import { BROWSERS, USB_MODES, triggerLabel } from "./constants";
import { errorText, useDlpDevice, useDlpPerms, useInvalidateDlp } from "./hooks";
import { ScanRequestModal } from "./ScanRequestModal";

export function DevicePanel({ id, onClose }: { id: number; onClose: () => void }) {
  const api = useApi();
  const { canOperate, canAdmin, hasExitScan, hasUsb, hasUpload } = useDlpPerms();
  const invalidate = useInvalidateDlp();
  const query = useDlpDevice(Number.isFinite(id) ? id : null);
  const d = query.data;
  const [requesting, setRequesting] = useState(false);

  const setMode = useMutation({
    mutationFn: (usb_mode: DlpUsbMode | null) => api.dlpPatchDevice(id, { usb_mode }),
    onSuccess: () => {
      void message.success("USB モードを変えた(次の poll で端末に届く)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const resetKey = useMutation({
    mutationFn: () => api.dlpResetDeviceKey(id),
    onSuccess: () => {
      void message.success("端末の鍵をリセットした(次の poll で新しい鍵を発行する)");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  if (query.isError) {
    return (
      <SidePanel title="端末" onClose={onClose}>
        <Alert type="error" showIcon message="端末を取得できなかった" description={errorText(query.error)} />
      </SidePanel>
    );
  }

  return (
    <SidePanel
      title={d?.hostname || d?.serial || "端末"}
      ariaLabel={`端末 ${d?.hostname ?? id} の詳細`}
      loading={query.isLoading || !d}
      status={d && usbModeTag(d.usb_mode)}
      sub={d && <span className="ui-num">{[d.serial, d.os].filter(Boolean).join(" · ")}</span>}
      barActions={
        d && canOperate && hasExitScan ? (
          <Button type="primary" size="small" onClick={() => setRequesting(true)}>
            スキャンを依頼
          </Button>
        ) : undefined
      }
      onClose={onClose}
    >
      {d && (
        <>
          <PanelSection title="基本情報">
            <KeyValueList
              narrow
              items={[
                { label: "製造番号", value: d.serial, copyable: true },
                { label: "ホスト名", value: d.hostname },
                { label: "OS", value: d.os },
                { label: "従業員", value: employeeCell(d.employee), always: true },
                { label: "エージェント", value: d.agent_version },
                { label: "機能", value: d.capabilities.join(" / ") },
                { label: "設定版", value: <span className="ui-num">{d.config_version}</span> },
                { label: "最終 poll", value: d.last_poll ? <TimeAgo iso={d.last_poll} /> : "", always: true },
                { label: "最終 report", value: d.last_report ? <TimeAgo iso={d.last_report} /> : "" },
                {
                  label: "デバイス台帳",
                  value: d.mdm_device_id ? <Link to={`/mdm/devices/${d.mdm_device_id}`}>#{d.mdm_device_id}</Link> : "",
                },
              ]}
            />
          </PanelSection>
          {hasUsb && (
            <PanelSection title="USB モード">
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Select
                  style={{ width: "100%" }}
                  value={d.usb_mode ?? ""}
                  disabled={!canAdmin || setMode.isPending}
                  loading={setMode.isPending}
                  onChange={(v: string) => setMode.mutate((v || null) as DlpUsbMode | null)}
                  options={[{ value: "", label: "既定に従う(設定の usb_mode_default)" }, ...USB_MODES]}
                />
                <span style={{ color: "var(--ux-text-tertiary)", fontSize: 12 }}>
                  読み取り専用は WriteProtect、遮断は USBSTOR の停止。レジストリで効くので反映は次の poll
                  {canAdmin ? "" : "(変更は dlp:admin)"}
                </span>
              </Space>
            </PanelSection>
          )}
          {canAdmin && (
            <PanelSection title="端末の鍵">
              <Space direction="vertical" size={4} style={{ width: "100%" }}>
                <Popconfirm
                  title="端末の鍵をリセットする"
                  description="エージェントを入れ直した端末だけに使う。次の poll で新しい鍵を発行する"
                  okText="リセット"
                  cancelText="やめる"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => resetKey.mutate()}
                >
                  <Button size="small" danger loading={resetKey.isPending}>
                    端末の鍵をリセット
                  </Button>
                </Popconfirm>
                <span style={{ color: "var(--ux-text-tertiary)", fontSize: 12 }}>
                  鍵は初回の poll で発行される。リセット後に最初に poll した端末が新しい鍵を受け取る
                </span>
              </Space>
            </PanelSection>
          )}
          {hasExitScan && (
            <PanelSection title={`直近のスキャン(${d.scans.length})`}>
              {d.scans.length ? (
                <ul className="ui-plain-list">
                  {d.scans.map((s) => (
                    <li key={s.id}>
                      <Space size={6} wrap>
                        <Link to={`/dlp/scans?panel=${s.id}`} className="ui-num">
                          #{s.id}
                        </Link>
                        {scanStatusTag(s)}
                        <span>{triggerLabel(s.trigger)}</span>
                        <span className="ui-num">検出 {s.findings_count}</span>
                        <TimeAgo iso={s.created_at} />
                      </Space>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: "var(--ux-text-disabled)" }}>なし</div>
              )}
            </PanelSection>
          )}
          {hasUsb && (
            <PanelSection title={`直近の USB(${d.usb_events.length})`}>
              {d.usb_events.length ? (
                <ul className="ui-plain-list">
                  {d.usb_events.map((e) => (
                    <li key={e.id}>
                      <Space size={6} wrap>
                        {usbKindTag(e.kind)}
                        <span>{[e.drive, e.label].filter(Boolean).join(" ")}</span>
                        <span style={{ color: "var(--ux-text-tertiary)" }}>{e.username}</span>
                        <TimeAgo iso={e.at} />
                      </Space>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: "var(--ux-text-disabled)" }}>なし</div>
              )}
              <div style={{ marginTop: 6 }}>
                <Link to={`/dlp/usb?device_id=${d.id}`}>この端末の USB ログをすべて見る</Link>
              </div>
            </PanelSection>
          )}
          {(hasUpload || d.upload_status) && (
            <PanelSection title="アップロード観測">
              {d.upload_status ? (
                <>
                  <KeyValueList
                    narrow
                    items={[
                      { label: "状態", value: uploadStateTag(d.upload_status.state), always: true },
                      { label: "エラー", value: d.upload_status.error ?? "" },
                      {
                        label: "適用した設定版",
                        value: d.upload_status.applied_version == null ? "" : <span className="ui-num">{d.upload_status.applied_version}</span>,
                      },
                      {
                        label: "取りこぼし",
                        value: (
                          <span className="ui-num">
                            ETW {d.upload_status.events_lost} / 処理 {d.upload_status.app_dropped} / 溢れ {d.upload_status.overflow}
                          </span>
                        ),
                      },
                      { label: "最終観測", value: d.upload_status.last_event_at ? <TimeAgo iso={d.upload_status.last_event_at} /> : "" },
                      { label: "報告", value: d.upload_status_at ? <TimeAgo iso={d.upload_status_at} /> : "" },
                    ]}
                  />
                  <div style={{ marginTop: 8, fontSize: 12, color: "var(--ux-text-tertiary)" }}>ブラウザのポリシー(遮断 / DoH 停止)</div>
                  <KeyValueList
                    narrow
                    items={BROWSERS.map((b) => ({
                      label: b.label,
                      always: true,
                      value: (
                        <Space size={4} wrap>
                          <span>遮断</span>
                          {browserPolicyTag(d.upload_status?.block?.[b.value] ?? "off")}
                          <span>DoH</span>
                          {browserPolicyTag(d.upload_status?.doh?.[b.value] ?? "off")}
                        </Space>
                      ),
                    }))}
                  />
                </>
              ) : (
                <div style={{ color: "var(--ux-text-disabled)" }}>
                  報告なし(エージェントがアップロード観測に対応していないか、まだ poll していない)
                </div>
              )}
            </PanelSection>
          )}
          {hasUpload && (
            <PanelSection title={`直近のアップロード検知(${d.upload_events?.length ?? 0})`}>
              {d.upload_events?.length ? (
                <ul className="ui-plain-list">
                  {d.upload_events.map((e) => (
                    <li key={e.id}>
                      <Space size={6} wrap>
                        {uploadKindTag(e.kind, e.attribution)}
                        <span className="ui-num">{e.rule_domain ?? "宛先不明"}</span>
                        <span className="ui-num">{bytesText(e.bytes_sent)}</span>
                        {uploadAttributionTag(e.attribution)}
                        <span style={{ color: "var(--ux-text-tertiary)" }}>{e.process}</span>
                        <TimeAgo iso={e.window_end} />
                      </Space>
                    </li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: "var(--ux-text-disabled)" }}>なし</div>
              )}
              <div style={{ marginTop: 6 }}>
                <Link to={`/dlp/upload?device_id=${d.id}`}>この端末のアップロード検知をすべて見る</Link>
              </div>
            </PanelSection>
          )}
        </>
      )}
      <ScanRequestModal
        open={requesting}
        deviceId={id}
        onClose={() => setRequesting(false)}
        onCreated={() => setRequesting(false)}
      />
    </SidePanel>
  );
}
